import * as THREE from "three";
import { Tiles } from "./blocks";

export const TILE_SIZE = 16;
export const ATLAS_COLS = 16;
export const ATLAS_ROWS = 4;
export const ATLAS_TILES = ATLAS_COLS * ATLAS_ROWS;

function hash(x: number, y: number, s: number): number {
  let n = Math.imul(x, 374761393) ^ Math.imul(y, 668265263) ^ Math.imul(s, 1274126177);
  n = Math.imul(n ^ (n >>> 13), 1274126177);
  return ((n ^ (n >>> 16)) >>> 0) / 4294967296;
}

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function rgb(r: number, g: number, b: number, j = 0): [number, number, number] {
  return [
    Math.max(0, Math.min(255, r + j)),
    Math.max(0, Math.min(255, g + j)),
    Math.max(0, Math.min(255, b + j)),
  ];
}

type Painter = (x: number, y: number) => [number, number, number, number];

function paintTile(
  data: Uint8ClampedArray,
  col: number,
  row: number,
  atlasW: number,
  fn: Painter,
): void {
  const ox = col * TILE_SIZE;
  const oy = row * TILE_SIZE;
  for (let y = 0; y < TILE_SIZE; y++) {
    for (let x = 0; x < TILE_SIZE; x++) {
      const [r, g, b, a] = fn(x, y);
      const i = ((oy + y) * atlasW + (ox + x)) * 4;
      data[i] = r;
      data[i + 1] = g;
      data[i + 2] = b;
      data[i + 3] = a;
    }
  }
}

function speckle(
  x: number,
  y: number,
  seed: number,
  base: [number, number, number],
  varAmt: number,
  grain = 1,
): [number, number, number, number] {
  const n = hash(x, y, seed);
  const n2 = hash(x + 3, y + 7, seed + 9);
  const j = (n - 0.5) * varAmt * 2;
  let [r, g, b] = rgb(base[0] + j, base[1] + j, base[2] + j);
  if (n2 > 0.86) {
    const d = (n2 - 0.86) * 80 * grain;
    r = Math.max(0, r - d);
    g = Math.max(0, g - d);
    b = Math.max(0, b - d);
  }
  return [r, g, b, 255];
}

function makePainters(): Painter[] {
  const p: Painter[] = new Array(ATLAS_TILES).fill((x: number, y: number) =>
    speckle(x, y, 1, [180, 180, 180], 12),
  );

  p[Tiles.bedrock] = (x, y) => speckle(x, y, 11, [42, 42, 48], 22);
  p[Tiles.stone] = (x, y) => speckle(x, y, 12, [118, 118, 124], 18);
  p[Tiles.dirt] = (x, y) => speckle(x, y, 13, [118, 82, 52], 20);
  p[Tiles.grassTop] = (x, y) => {
    const blade = hash(x, y, 21) > 0.55;
    const base: [number, number, number] = blade ? [92, 168, 64] : [78, 148, 56];
    return speckle(x, y, 14, base, 22);
  };
  p[Tiles.grassSide] = (x, y) => {
    if (y < 5) {
      const t = 1 - y / 5;
      const g = speckle(x, y, 14, [86, 156, 60], 18);
      const d = speckle(x, y, 13, [118, 82, 52], 16);
      return [
        lerp(d[0], g[0], t),
        lerp(d[1], g[1], t),
        lerp(d[2], g[2], t),
        255,
      ];
    }
    return speckle(x, y, 13, [118, 82, 52], 16);
  };
  p[Tiles.sand] = (x, y) => speckle(x, y, 15, [214, 196, 128], 16);
  p[Tiles.gravel] = (x, y) => speckle(x, y, 16, [124, 118, 112], 28, 1.4);
  p[Tiles.clay] = (x, y) => speckle(x, y, 17, [156, 140, 124], 14);
  p[Tiles.snow] = (x, y) => speckle(x, y, 18, [236, 242, 246], 10);
  p[Tiles.ice] = (x, y) => {
    const [r, g, b] = speckle(x, y, 19, [156, 204, 222], 18);
    return [r, g, b, 180];
  };
  p[Tiles.water] = (x, y) => {
    const w = Math.sin(x * 0.8) * Math.cos(y * 0.6);
    return [36 + w * 10, 132 + w * 16, 168 + w * 12, 160];
  };
  p[Tiles.oakLog] = (x, y) => {
    const ring = x < 2 || x > 13;
    return speckle(x, y, 20, ring ? [86, 62, 36] : [124, 88, 48], 14);
  };
  p[Tiles.oakLogTop] = (x, y) => {
    const dx = x - 7.5;
    const dy = y - 7.5;
    const r = Math.sqrt(dx * dx + dy * dy);
    const ring = Math.sin(r * 1.6) > 0.2;
    return speckle(x, y, 21, ring ? [150, 118, 72] : [108, 78, 44], 12);
  };
  p[Tiles.oakLeaves] = (x, y) => {
    const hole = hash(x, y, 40) < 0.18;
    if (hole) return [0, 0, 0, 0];
    return speckle(x, y, 22, [62, 132, 52], 24);
  };
  p[Tiles.birchLog] = (x, y) => {
    const stripe = hash(x, Math.floor(y / 2), 23) > 0.82 || x < 1 || x > 14;
    return speckle(x, y, 23, stripe ? [62, 58, 52] : [226, 222, 208], 10);
  };
  p[Tiles.birchLogTop] = (x, y) => speckle(x, y, 24, [210, 200, 176], 12);
  p[Tiles.birchLeaves] = (x, y) => {
    if (hash(x, y, 41) < 0.16) return [0, 0, 0, 0];
    return speckle(x, y, 25, [156, 196, 78], 22);
  };
  p[Tiles.pineLog] = (x, y) => speckle(x, y, 26, [78, 56, 40], 14);
  p[Tiles.pineLogTop] = (x, y) => speckle(x, y, 27, [96, 72, 48], 12);
  p[Tiles.pineLeaves] = (x, y) => {
    if (hash(x, y, 42) < 0.12) return [0, 0, 0, 0];
    return speckle(x, y, 28, [28, 92, 62], 18);
  };
  p[Tiles.cherryLog] = (x, y) => speckle(x, y, 29, [92, 62, 52], 12);
  p[Tiles.cherryLogTop] = (x, y) => speckle(x, y, 30, [164, 118, 96], 10);
  p[Tiles.cherryLeaves] = (x, y) => {
    if (hash(x, y, 43) < 0.2) return [0, 0, 0, 0];
    const blush = hash(x, y, 44) > 0.55;
    return speckle(x, y, 31, blush ? [236, 140, 176] : [242, 176, 198], 16);
  };
  p[Tiles.jungleLog] = (x, y) => speckle(x, y, 32, [92, 58, 28], 14);
  p[Tiles.jungleLogTop] = (x, y) => speckle(x, y, 33, [128, 86, 42], 12);
  p[Tiles.jungleLeaves] = (x, y) => {
    if (hash(x, y, 45) < 0.14) return [0, 0, 0, 0];
    return speckle(x, y, 34, [18, 124, 48], 20);
  };
  p[Tiles.cactus] = (x, y) => {
    const rib = x === 3 || x === 12;
    return speckle(x, y, 35, rib ? [36, 108, 52] : [52, 140, 64], 12);
  };
  p[Tiles.cactusTop] = (x, y) => speckle(x, y, 36, [48, 132, 60], 12);
  p[Tiles.oakPlanks] = (x, y) => {
    const line = y % 4 === 3;
    const seam = x === 7 || x === 8;
    return speckle(x, y, 37, line || seam ? [96, 68, 36] : [176, 132, 72], 10);
  };
  p[Tiles.cobble] = (x, y) => {
    const cell = hash(Math.floor(x / 4), Math.floor(y / 4), 38);
    const grout = x % 4 === 0 || y % 4 === 0;
    const base = 96 + cell * 40;
    return speckle(x, y, 38, grout ? [72, 72, 76] : [base, base, base + 4], 10);
  };
  p[Tiles.glass] = (x, y) => {
    const edge = x === 0 || y === 0 || x === 15 || y === 15;
    return edge ? [200, 220, 230, 140] : [180, 210, 220, 50];
  };
  p[Tiles.sandstone] = (x, y) => {
    const band = Math.floor(y / 4) % 2;
    return speckle(x, y, 39, band ? [214, 190, 128] : [198, 172, 108], 10);
  };
  p[Tiles.coalOre] = (x, y) => {
    const stone = speckle(x, y, 12, [118, 118, 124], 14);
    if (hash(x, y, 50) > 0.78) return speckle(x, y, 50, [28, 28, 32], 10);
    return stone;
  };
  p[Tiles.ironOre] = (x, y) => {
    const stone = speckle(x, y, 12, [118, 118, 124], 14);
    if (hash(x, y, 51) > 0.8) return speckle(x, y, 51, [196, 168, 140], 10);
    return stone;
  };
  p[Tiles.goldOre] = (x, y) => {
    const stone = speckle(x, y, 12, [118, 118, 124], 14);
    if (hash(x, y, 52) > 0.8) return speckle(x, y, 52, [232, 188, 64], 10);
    return stone;
  };
  p[Tiles.diamondOre] = (x, y) => {
    const stone = speckle(x, y, 12, [118, 118, 124], 14);
    if (hash(x, y, 53) > 0.82) return speckle(x, y, 53, [72, 220, 214], 10);
    return stone;
  };
  p[Tiles.craftTop] = (x, y) => {
    const grid = x === 5 || x === 10 || y === 5 || y === 10;
    return speckle(x, y, 54, grid ? [92, 62, 32] : [168, 118, 62], 10);
  };
  p[Tiles.craftSide] = (x, y) => speckle(x, y, 55, [156, 108, 56], 12);
  p[Tiles.furnaceFront] = (x, y) => {
    const mouth = x > 4 && x < 11 && y > 6 && y < 13;
    if (mouth) return speckle(x, y, 56, [28, 22, 20], 8);
    return speckle(x, y, 38, [110, 110, 114], 12);
  };
  p[Tiles.furnaceSide] = (x, y) => speckle(x, y, 38, [110, 110, 114], 12);
  p[Tiles.chestFront] = (x, y) => {
    const latch = x > 6 && x < 9 && y > 7 && y < 10;
    if (latch) return [232, 196, 72, 255];
    const band = y === 7 || y === 8;
    return speckle(x, y, 57, band ? [92, 58, 28] : [164, 108, 48], 10);
  };
  p[Tiles.chestSide] = (x, y) => speckle(x, y, 58, [156, 102, 44], 10);
  p[Tiles.bricks] = (x, y) => {
    const row = Math.floor(y / 4);
    const ox = row % 2 === 0 ? 0 : 4;
    const grout = y % 4 === 3 || (x + ox) % 8 === 0;
    return speckle(x, y, 59, grout ? [92, 78, 70] : [156, 78, 58], 10);
  };
  p[Tiles.tallGrass] = (x, y) => {
    const stem = Math.abs(x - 8) < 1 + (15 - y) / 8 && y > 2;
    const blade = hash(x, y, 60) > 0.7 && y > 4 && Math.abs(x - 8) < 5;
    if (stem || blade) return speckle(x, y, 60, [86, 160, 58], 18);
    return [0, 0, 0, 0];
  };
  p[Tiles.flowerRed] = (x, y) => flower(x, y, [204, 48, 56]);
  p[Tiles.flowerYellow] = (x, y) => flower(x, y, [232, 196, 56]);
  p[Tiles.flowerPink] = (x, y) => flower(x, y, [236, 132, 176]);
  p[Tiles.mushroom] = (x, y) => {
    const stem = Math.abs(x - 8) < 1 && y > 6;
    const cap = (x - 8) * (x - 8) + (y - 5) * (y - 5) * 1.4 < 16 && y < 8;
    if (cap) return speckle(x, y, 64, [168, 48, 42], 12);
    if (stem) return speckle(x, y, 65, [214, 200, 176], 8);
    return [0, 0, 0, 0];
  };
  p[Tiles.pumpkinSide] = (x, y) => {
    const groove = x % 4 === 0;
    return speckle(x, y, 66, groove ? [168, 92, 24] : [214, 132, 36], 10);
  };
  p[Tiles.pumpkinTop] = (x, y) => speckle(x, y, 67, [72, 112, 36], 10);
  p[Tiles.ironBlock] = (x, y) => {
    const edge = x < 1 || y < 1 || x > 14 || y > 14;
    return speckle(x, y, 68, edge ? [140, 144, 150] : [198, 204, 210], 8);
  };
  p[Tiles.goldBlock] = (x, y) => {
    const edge = x < 1 || y < 1 || x > 14 || y > 14;
    return speckle(x, y, 69, edge ? [176, 136, 36] : [236, 196, 72], 8);
  };
  p[Tiles.diamondBlock] = (x, y) => {
    const edge = x < 1 || y < 1 || x > 14 || y > 14;
    return speckle(x, y, 70, edge ? [40, 148, 150] : [72, 220, 214], 8);
  };
  p[Tiles.glowstone] = (x, y) => speckle(x, y, 71, [232, 196, 96], 22);
  p[Tiles.snowySide] = (x, y) => {
    if (y < 4) return speckle(x, y, 18, [236, 242, 246], 8);
    return speckle(x, y, 13, [118, 82, 52], 16);
  };
  p[Tiles.birchPlanks] = (x, y) => {
    const line = y % 4 === 3;
    return speckle(x, y, 72, line ? [168, 156, 124] : [226, 214, 176], 8);
  };

  return p;
}

function flower(x: number, y: number, color: [number, number, number]): [number, number, number, number] {
  const stem = Math.abs(x - 8) < 1 && y > 7;
  const petal = (x - 8) * (x - 8) + (y - 5) * (y - 5) < 10;
  const center = (x - 8) * (x - 8) + (y - 5) * (y - 5) < 2;
  if (center) return [236, 208, 64, 255];
  if (petal) return speckle(x, y, 61, color, 14);
  if (stem) return speckle(x, y, 62, [52, 132, 48], 10);
  return [0, 0, 0, 0];
}

export interface Atlas {
  texture: THREE.CanvasTexture;
  cols: number;
  rows: number;
  canvas: HTMLCanvasElement;
}

export function createAtlas(): Atlas {
  const w = ATLAS_COLS * TILE_SIZE;
  const h = ATLAS_ROWS * TILE_SIZE;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(w, h);
  const painters = makePainters();
  for (let i = 0; i < ATLAS_TILES; i++) {
    const col = i % ATLAS_COLS;
    const row = Math.floor(i / ATLAS_COLS);
    paintTile(img.data, col, row, w, painters[i]!);
  }
  ctx.putImageData(img, 0, 0);
  const texture = new THREE.CanvasTexture(canvas);
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.NearestMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return { texture, cols: ATLAS_COLS, rows: ATLAS_ROWS, canvas };
}

export function tileUv(tile: number): { u: number; v: number } {
  const col = tile % ATLAS_COLS;
  const row = Math.floor(tile / ATLAS_COLS);
  return { u: col / ATLAS_COLS, v: 1 - (row + 1) / ATLAS_ROWS };
}

export function tileSizeUv(): { du: number; dv: number } {
  return { du: 1 / ATLAS_COLS, dv: 1 / ATLAS_ROWS };
}
